import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, FunnelChart, Funnel, LabelList, Cell,
} from 'recharts';
import {
  FlaskConical, TrendingUp, Users, MousePointerClick, Award,
  AlertTriangle, CheckCircle2, Minus, RefreshCw, Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANT_COLORS = { A: '#004ac6', B: '#7c3aed' };

/**
 * SafeChart — wraps a chart render-function with a ResizeObserver gate.
 * Only mounts the Recharts tree once the container has measured width > 0,
 * eliminating "width=X height=-1 are set incorrectly" console warnings caused
 * by page-transition transforms.
 */
function SafeChart({ height, children }) {
  const ref = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    let rafId;
    let mounted = true;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0 && mounted) {
        setDims({ width: w, height: h });
        return true;
      }
      return false;
    };
    // Initial check
    if (measure()) return;
    // ResizeObserver for layout changes
    const ro = new ResizeObserver(() => { measure(); });
    ro.observe(el);
    // Fallback poll via rAF for up to ~1s in case ResizeObserver fires late
    let tries = 0;
    const tick = () => {
      tries += 1;
      if (measure() || tries > 60 || !mounted) return;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => { mounted = false; ro.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, []);
  const ready = dims.width > 0 && dims.height > 0;
  return (
    <div ref={ref} style={{ width: '100%', height, minWidth: 0 }}>
      {ready ? (
        <ResponsiveContainer width={dims.width} height={dims.height} minWidth={0}>
          {children}
        </ResponsiveContainer>
      ) : (
        <ChartSkeleton h={height} />
      )}
    </div>
  );
}

function ChartSkeleton({ h }) {
  return (
    <div className="w-full flex items-center justify-center animate-pulse bg-gradient-to-r from-[#eff4ff] via-white to-[#eff4ff] rounded-md" style={{ height: h }}>
      <span className="text-[10px] uppercase tracking-widest text-[#c3c6d7]">Rendering chart…</span>
    </div>
  );
}

const EXP_META = {
  landing_hero_headline: {
    label: 'Hero Headline',
    desc: 'Rotating targets headline vs static "Every country. Every sector."',
    a_label: 'A · Rotating targets',
    b_label: 'B · Static "Every country"',
  },
  landing_cta_copy: {
    label: 'Primary CTA Copy',
    desc: '"Generate your first blueprint" vs "Start free trial"',
    a_label: 'A · Generate your first blueprint',
    b_label: 'B · Start free trial',
  },
};

function SignificanceBadge({ sig }) {
  if (!sig) return null;
  if (sig.insufficient_data) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f3f4f6] text-[#434655] text-[10px] font-bold uppercase tracking-widest">
        <Minus className="w-3 h-3" /> Collecting · n &lt; 30
      </span>
    );
  }
  if (sig.significant) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ecfdf5] text-[#00A651] text-[10px] font-bold uppercase tracking-widest">
        <CheckCircle2 className="w-3 h-3" /> Significant · {sig.confidence_pct}% conf
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#fef3c7] text-[#b45309] text-[10px] font-bold uppercase tracking-widest">
      <AlertTriangle className="w-3 h-3" /> Not yet · {sig.confidence_pct}% conf
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, color = '#004ac6' }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border-t-4" style={{ borderTopColor: color }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#434655]">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '1A' }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-extrabold tracking-tighter text-[#0b1c30]">{value}</div>
      {sublabel && <p className="text-[11px] text-[#434655] mt-1">{sublabel}</p>}
    </div>
  );
}

function ExperimentCard({ exp, data }) {
  const meta = EXP_META[exp] || { label: exp, desc: '', a_label: 'Variant A', b_label: 'Variant B' };
  const sig = data._significance;
  const a = data.A || {};
  const b = data.B || {};

  // Defer chart mount until after layout to avoid Recharts width=0/height=-1 warnings
  // (page-transition animation uses translateY which can confuse initial measurement)
  // NOTE: SafeChart now handles this per-chart via ResizeObserver; keep for back-compat.
  const [chartsReady, setChartsReady] = useState(true);

  const a_imp = a.impression || 0;
  const b_imp = b.impression || 0;
  const a_click = a.cta_primary_click || 0;
  const b_click = b.cta_primary_click || 0;
  const a_sec = a.cta_secondary_click || 0;
  const b_sec = b.cta_secondary_click || 0;

  // Bar chart: impressions / clicks per variant
  const barData = [
    { metric: 'Impressions', A: a_imp, B: b_imp },
    { metric: 'Primary clicks', A: a_click, B: b_click },
    { metric: 'Secondary clicks', A: a_sec, B: b_sec },
  ];

  // CTR comparison bar
  const ctrData = [
    { metric: 'Primary CTR %', A: a.ctr_primary_pct || 0, B: b.ctr_primary_pct || 0 },
    { metric: 'Secondary CTR %', A: a.ctr_secondary_pct || 0, B: b.ctr_secondary_pct || 0 },
  ];

  // Funnel per variant (stacked)
  const funnelA = [
    { name: 'Impressions', value: a_imp, fill: '#004ac6' },
    { name: 'Primary Click', value: a_click, fill: '#1e6bff' },
    { name: 'Secondary Click', value: a_sec, fill: '#6495ff' },
  ];
  const funnelB = [
    { name: 'Impressions', value: b_imp, fill: '#7c3aed' },
    { name: 'Primary Click', value: b_click, fill: '#a78bfa' },
    { name: 'Secondary Click', value: b_sec, fill: '#c4b5fd' },
  ];

  const winnerLabel = sig?.winner === 'A'
    ? meta.a_label
    : sig?.winner === 'B' ? meta.b_label : '—';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#c3c6d7]/30 overflow-hidden">
      <div className="p-5 border-b border-[#c3c6d7]/20 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <FlaskConical className="w-4 h-4 text-[#7c3aed]" />
            <h3 className="text-lg font-extrabold tracking-tighter text-[#0b1c30]">{meta.label}</h3>
            <SignificanceBadge sig={sig} />
          </div>
          <p className="text-xs text-[#434655]">{meta.desc}</p>
          <p className="text-[10px] text-[#737686] mt-1 font-mono">{exp}</p>
        </div>
        <div className="bg-gradient-to-br from-[#f8f9ff] to-white border border-[#c3c6d7]/30 rounded-lg px-4 py-2 flex items-center gap-3">
          <Trophy className="w-4 h-4 text-[#f59e0b]" />
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#434655]">Leading</div>
            <div className="text-xs font-bold text-[#0b1c30]">{winnerLabel}</div>
          </div>
        </div>
      </div>

      {/* Variant summary row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5 border-b border-[#c3c6d7]/20 bg-[#f8f9ff]/50">
        {['A', 'B'].map(v => {
          const d = v === 'A' ? a : b;
          const lbl = v === 'A' ? meta.a_label : meta.b_label;
          return (
            <div key={v} className="bg-white rounded-lg p-4 border border-[#c3c6d7]/30" data-testid={`variant-summary-${exp}-${v}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 rounded-md text-white flex items-center justify-center text-xs font-black" style={{ backgroundColor: VARIANT_COLORS[v] }}>{v}</span>
                <span className="text-xs font-bold text-[#0b1c30] truncate">{lbl}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737686]">Imp</div>
                  <div className="text-base font-extrabold text-[#0b1c30]">{d.impression || 0}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737686]">Clicks</div>
                  <div className="text-base font-extrabold text-[#0b1c30]">{d.cta_primary_click || 0}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737686]">CTR</div>
                  <div className="text-base font-extrabold" style={{ color: VARIANT_COLORS[v] }}>{d.ctr_primary_pct || 0}%</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737686]">Sessions</div>
                  <div className="text-base font-extrabold text-[#0b1c30]">{d.unique_sessions || 0}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-5">
        <div className="bg-[#f8f9ff]/50 rounded-lg p-4 border border-[#c3c6d7]/20">
          <h4 className="text-xs font-bold uppercase tracking-widest text-[#434655] mb-3">Volume · A vs B</h4>
          <SafeChart height={260}>
            <BarChart data={barData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5eeff" />
              <XAxis dataKey="metric" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="A" fill={VARIANT_COLORS.A} radius={[4, 4, 0, 0]} />
              <Bar dataKey="B" fill={VARIANT_COLORS.B} radius={[4, 4, 0, 0]} />
            </BarChart>
          </SafeChart>
        </div>

        <div className="bg-[#f8f9ff]/50 rounded-lg p-4 border border-[#c3c6d7]/20">
          <h4 className="text-xs font-bold uppercase tracking-widest text-[#434655] mb-3">Click-through rate · %</h4>
          <SafeChart height={260}>
            <BarChart data={ctrData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5eeff" />
              <XAxis dataKey="metric" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="A" fill={VARIANT_COLORS.A} radius={[4, 4, 0, 0]} />
              <Bar dataKey="B" fill={VARIANT_COLORS.B} radius={[4, 4, 0, 0]} />
            </BarChart>
          </SafeChart>
        </div>

        <div className="bg-[#f8f9ff]/50 rounded-lg p-4 border border-[#c3c6d7]/20">
          <h4 className="text-xs font-bold uppercase tracking-widest text-[#434655] mb-3">Funnel · Variant A</h4>
          <SafeChart height={240}>
            <FunnelChart>
              <Tooltip />
              <Funnel dataKey="value" data={funnelA} isAnimationActive>
                <LabelList position="right" fill="#0b1c30" stroke="none" fontSize={11} dataKey="name" />
              </Funnel>
            </FunnelChart>
          </SafeChart>
        </div>

        <div className="bg-[#f8f9ff]/50 rounded-lg p-4 border border-[#c3c6d7]/20">
          <h4 className="text-xs font-bold uppercase tracking-widest text-[#434655] mb-3">Funnel · Variant B</h4>
          <SafeChart height={240}>
            <FunnelChart>
              <Tooltip />
              <Funnel dataKey="value" data={funnelB} isAnimationActive>
                <LabelList position="right" fill="#0b1c30" stroke="none" fontSize={11} dataKey="name" />
              </Funnel>
            </FunnelChart>
          </SafeChart>
        </div>
      </div>

      {/* Math details */}
      {sig && !sig.insufficient_data && (
        <div className="px-5 py-3 bg-gradient-to-r from-[#f8f9ff] to-white border-t border-[#c3c6d7]/20 flex items-center gap-6 flex-wrap text-[11px] text-[#434655]">
          <span className="font-bold uppercase tracking-widest text-[10px] text-[#737686]">Z-test</span>
          <span>z = <strong className="font-mono text-[#0b1c30]">{sig.z}</strong></span>
          <span>p-value = <strong className="font-mono text-[#0b1c30]">{sig.p_value}</strong></span>
          <span>confidence = <strong className="font-mono" style={{ color: sig.significant ? '#00A651' : '#b45309' }}>{sig.confidence_pct}%</strong></span>
          <span className="text-[10px] text-[#737686]">threshold: p &lt; 0.05 · min n ≥ 30 per variant</span>
        </div>
      )}
    </div>
  );
}

export default function AdminABEventsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/ab-events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      setData({ experiments: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const summary = useMemo(() => {
    if (!data) return null;
    const experiments = data.experiments || {};
    let totalImp = 0, totalClicks = 0, totalSecClicks = 0;
    const expCount = Object.keys(experiments).length;
    Object.values(experiments).forEach(variants => {
      ['A', 'B'].forEach(v => {
        const m = variants[v] || {};
        totalImp += m.impression || 0;
        totalClicks += m.cta_primary_click || 0;
        totalSecClicks += m.cta_secondary_click || 0;
      });
    });
    const ctr = totalImp ? ((totalClicks / totalImp) * 100).toFixed(2) : '0.00';
    return { totalImp, totalClicks, totalSecClicks, ctr, expCount };
  }, [data]);

  const experiments = (data && data.experiments) || {};

  return (
    <div className="min-h-screen bg-[#f8f9ff]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="admin-ab-events-page">
      <KineticHero
        uid="abev"
        palette="violet"
        eyebrow="Experimentation · Z-test significance"
        title="A/B Events"
        accent="Intelligence"
        subtitle="Real-time conversion funnels and statistical confidence for all running landing-page experiments."
        right={
          <Button onClick={fetchData} data-testid="ab-events-refresh" variant="outline" className="border-[#c3c6d7]/40 bg-white">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />
      <div className="p-6 lg:p-10">
      <div className="max-w-7xl mx-auto">
        {/* Summary stats */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FlaskConical} label="Active experiments" value={summary.expCount} color="#7c3aed" />
            <StatCard icon={Users} label="Total impressions" value={summary.totalImp.toLocaleString()} color="#004ac6" />
            <StatCard icon={MousePointerClick} label="Primary clicks" value={summary.totalClicks.toLocaleString()} color="#00A651" />
            <StatCard icon={TrendingUp} label="Overall CTR" value={`${summary.ctr}%`} sublabel="Primary CTA" color="#f59e0b" />
          </div>
        )}

        {/* Experiments */}
        {loading && !data && (
          <div className="bg-white rounded-xl p-12 text-center border border-[#c3c6d7]/30">
            <RefreshCw className="w-8 h-8 text-[#004ac6] animate-spin mx-auto mb-3" />
            <p className="text-sm text-[#434655]">Loading A/B events…</p>
          </div>
        )}

        {!loading && Object.keys(experiments).length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-[#c3c6d7]/30" data-testid="ab-events-empty">
            <FlaskConical className="w-10 h-10 text-[#c3c6d7] mx-auto mb-3" />
            <h3 className="text-lg font-bold text-[#0b1c30] mb-1">No experiments yet</h3>
            <p className="text-xs text-[#434655]">Impressions and clicks on the landing page will appear here in real-time.</p>
          </div>
        )}

        <div className="space-y-6">
          {Object.entries(experiments).map(([exp, variants]) => (
            <ExperimentCard key={exp} exp={exp} data={variants} />
          ))}
        </div>

        <div className="mt-8 text-[10px] text-[#737686] text-center">
          <Award className="w-3 h-3 inline mr-1" /> Confidence computed via two-proportion Z-test on primary CTA conversions.
        </div>
      </div>
      </div>
    </div>
  );
}
